import { App as AntdApp, Drawer, Descriptions, Typography, Space, Spin, Tag, Button, Popconfirm } from 'antd'
import { canManageKnowledge, KNOWLEDGE_STATUS_COLOR, KNOWLEDGE_STATUS_LABEL } from '@/modules/knowledge/constants'
import { useKnowledgeDetail, usePublishKnowledge, useArchiveKnowledge } from '@/modules/knowledge/queries'
import { useAuthStore } from '@/stores/authStore'

const { Text, Title, Paragraph } = Typography

interface Props {
  entryId: number | null
  onClose: () => void
}

export default function KnowledgeDetailDrawer({ entryId, onClose }: Props) {
  const { message } = AntdApp.useApp()
  const { data, isLoading, isError, error } = useKnowledgeDetail(entryId)
  const publishMutation = usePublishKnowledge()
  const archiveMutation = useArchiveKnowledge()
  const roles = useAuthStore((s) => s.roles)
  const canManage = canManageKnowledge(roles)

  const errorMessage = error instanceof Error ? error.message : '加载失败'

  const handlePublish = () => {
    if (!entryId) return
    publishMutation.mutate(
      { entryId },
      {
        onSuccess: () => message.success('发布成功'),
        onError: (err) => message.error(err instanceof Error ? err.message : '发布失败，当前角色可能无此权限'),
      },
    )
  }

  const handleArchive = () => {
    if (!entryId) return
    archiveMutation.mutate(
      { entryId },
      {
        onSuccess: () => message.success('归档成功'),
        onError: (err) => message.error(err instanceof Error ? err.message : '归档失败，当前角色可能无此权限'),
      },
    )
  }

  const isLoadingAction = publishMutation.isPending || archiveMutation.isPending

  return (
    <Drawer
      title="知识条目详情"
      size="large"
      open={!!entryId}
      onClose={onClose}
      destroyOnHidden
      extra={
        data && !isError ? (
          <Space>
            {canManage && data.status === 'draft' && (
              <Button
                type="primary"
                size="small"
                loading={isLoadingAction}
                onClick={handlePublish}
              >
                发布
              </Button>
            )}
            {canManage && data.status === 'published' && (
              <Popconfirm
                title="确定归档该知识条目？"
                description="归档后将从搜索列表中隐藏"
                onConfirm={handleArchive}
                okText="确定归档"
                cancelText="取消"
              >
                <Button size="small" loading={isLoadingAction} danger>
                  归档
                </Button>
              </Popconfirm>
            )}
          </Space>
        ) : null
      }
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : isError ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Text type="danger">{errorMessage}</Text>
        </div>
      ) : data ? (
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="条目ID">{data.id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={KNOWLEDGE_STATUS_COLOR[data.status]}>
                {KNOWLEDGE_STATUS_LABEL[data.status]}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="标题" span={2}>
              {data.title}
            </Descriptions.Item>
            <Descriptions.Item label="来源工单" span={2}>
              {data.source_ticket_id ? `#${data.source_ticket_id}` : <Text type="secondary">-</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="作者">{data.author}</Descriptions.Item>
            <Descriptions.Item label="审核人">{data.reviewer || '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{data.created_at}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{data.updated_at}</Descriptions.Item>
            {data.published_at && (
              <Descriptions.Item label="发布时间">{data.published_at}</Descriptions.Item>
            )}
          </Descriptions>

          {/* 关键词 */}
          {data.keywords && data.keywords.length > 0 && (
            <div>
              <Text strong>关键词</Text>
              <div style={{ marginTop: 8 }}>
                {data.keywords.map((kw, i) => (
                  <Tag key={i} color="blue">{kw}</Tag>
                ))}
              </div>
            </div>
          )}

          {/* 正文内容 */}
          <div>
            <Title level={5}>正文内容</Title>
            <Paragraph
              style={{
                marginTop: 8,
                background: '#fafafa',
                padding: 12,
                borderRadius: 4,
                whiteSpace: 'pre-wrap',
              }}
            >
              {data.content}
            </Paragraph>
          </div>
        </Space>
      ) : null}
    </Drawer>
  )
}
